/*
 * @Description:
 * @Author: Luke Z
 * @Date: 2021-03-12 14:26:48
 * @LastEditors: Luke Z
 * @LastEditTime: 2021-07-06 14:07:42
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelContentEditor/SalesChannelEditorTabbar/tools.ts
 */
import moment, { Moment } from 'moment';
import { uuid } from '@/src/tools/helper';
import { generateLinkImg } from '../common';
import { TabBarItem, TabBarSetting } from '@/src/types/common/SalesChannel';
import _ from 'lodash';
import { reduxUpdatedStatusControl } from 'src/tools/helper';

const convertDataFromServerToRedux = (data) => {
  const {
    defaultTabBar,
    inUse,
    pending,
    previous,
    original,
    timezoneGmt,
    translationEnabled = false,
  } = data;
  const defaultSetting = defaultTabBar
    ? transfer(defaultTabBar)
    : transfer(inUse);
  const originalSetting = transfer(original);
  let TabBarSettingList = [];
  if (inUse && !inUse.defaultVersion) {
    const s = transfer(inUse);
    TabBarSettingList.push(s);
  }
  if (Array.isArray(pending)) {
    const arr = pending.map((t) => transfer(t));
    TabBarSettingList = [...TabBarSettingList, ...arr];
  }
  if (Array.isArray(previous)) {
    const arr = previous.map((t) => transfer(t));
    TabBarSettingList = [...TabBarSettingList, ...arr];
  }
  const salesChannelContent = original ? original.salesChannelContent : '';
  const salesChannelName = original ? original.salesChannelName : '';
  const contentUuid = original ? original.contentUuid : '';
  return {
    defaultSetting,
    TabBarSettingList,
    originalSetting,
    timezoneGmt,
    salesChannelContent,
    salesChannelName,
    contentUuid,
    translationEnabled,
    ...reduxUpdatedStatusControl,
  };
};

const convertDataFromReduxToServer = (state) => {
  const tabBarState = _.cloneDeep(state);
  const {
    contentUuid,
    TabBarSettingList,
    defaultSetting,
    newTabBarSettingList,
  } = tabBarState;
  let result = [];
  if (Array.isArray(TabBarSettingList)) {
    const arr = TabBarSettingList.map(transformReduxTabBar);
    result = [...arr];
  }
  if (Array.isArray(newTabBarSettingList)) {
    const arr = newTabBarSettingList.map(transformReduxTabBar);
    result = [...result, ...arr];
  }
  if (defaultSetting) {
    result.push(transformReduxTabBar(defaultSetting));
  }
  return { body: result, uuid: contentUuid };
};

export const transfer = (setting = {} as any) => {
  const tabs = _.get(setting, 'tabItems.items');
  return {
    id: setting.uuid,
    originalId: setting.uuid,
    name: setting.name,
    display: setting.displayOption === 'Fixed' ? 'fixed' : 'scroll',
    backgroundColor: _.get(setting, 'colors.background'),
    inactiveColor: _.get(setting, 'colors.inactive'),
    activeColor: _.get(setting, 'colors.active'),
    isDefault: Boolean(setting.defaultVersion),
    tabs: transformTabsData(Array.isArray(tabs) ? tabs : []),
    startTime:
      setting.beginDate && setting.beginTime
        ? `${setting.beginDate} ${setting.beginTime}`
        : null,
    endTime:
      setting.endDate && setting.endTime
        ? `${setting.endDate} ${setting.endTime}`
        : null,
    showError: true,
  };
};

export const transformReduxTabBar = (tabBar: TabBarSetting) => {
  const result = {
    uuid: tabBar.originalId,
    defaultVersion: tabBar.isDefault,
    name: tabBar.name,
    beginDate: tabBar.startTime
      ? moment(tabBar.startTime).format('YYYY-MM-DD')
      : null,
    beginTime: tabBar.startTime
      ? moment(tabBar.startTime).format('HH:mm')
      : null,
    endDate: tabBar.endTime
      ? moment(tabBar.endTime).format('YYYY-MM-DD')
      : null,
    endTime: tabBar.endTime ? moment(tabBar.endTime).format('HH:mm') : null,
    displayOption: tabBar.display === 'fixed' ? 'Fixed' : 'ScrollOff',
    colors: {
      background: tabBar.backgroundColor,
      inactive: tabBar.inactiveColor,
      active: tabBar.activeColor,
    },
    tabItems: {
      items: Array.isArray(tabBar.tabs)
        ? tabBar.tabs.map((tab: TabBarItem) => ({
            landing: !!tab.isHome,
            title: tab.title,
            link: tab && tab.link ? tab.link : null,
            icon: tab.icon,
          }))
        : [],
    },
  };
  if (tabBar.isDefault) {
    delete result.beginDate;
    delete result.beginTime;
    delete result.endTime;
    delete result.endDate;
  }
  return result;
};

export const transformTabsData = (tabs: Array<any>) => {
  return tabs.map((tab) => ({
    draggableId: uuid(),
    isHome: tab.landing,
    title: tab.title,
    link: tab && tab.link ? tab.link : generateLinkImg(1)[0],
    icon: tab.icon,
  }));
};

export const generateTabBarSetting = (
  defaultSetting: Record<string, any>,
  index: number,
) => {
  return {
    ...defaultSetting,
    id: uuid(),
    originalId: '',
    isDefault: false,
    name: `Custom Tab Bar ${index}`,
    startTime: null,
    endTime: null,
  };
};

export const groupingTabBarSettingByTime = (
  defaultSetting,
  settingList: Array<any>,
  scTimezoneUTC: string,
) => {
  const inUse = [];
  let pending = [];
  let previous = [];
  const currentTime = moment().tz(scTimezoneUTC);
  if (settingList.length === 0) {
    return {
      inUse: [defaultSetting],
      pending,
      previous,
    };
  } else {
    settingList.forEach((setting) => {
      const { startTime, endTime }: { startTime: Moment; endTime: Moment } =
        setting || {};
      const start = moment.tz(startTime, scTimezoneUTC);
      const end = moment.tz(endTime, scTimezoneUTC);
      if (!start || !end) pending.push(setting);
      else {
        if (currentTime.isBetween(start, end)) inUse.push(setting);
        if (currentTime.isAfter(end)) previous.push(setting);
        if (currentTime.isBefore(start)) pending.push(setting);
      }
    });
    pending = sorByAscentTime(pending);
    previous = sortByDesendTime(previous);
    return {
      inUse,
      pending,
      previous,
    };
  }
};

export const sortByDesendTime = (list: Array<{ startTime: string }>) => {
  return list.sort((item1, item2) => {
    if (!item1.startTime) return -1;
    if (!item2.startTime) return 1;
    if (moment(item1.startTime).isAfter(item2.startTime)) return -1;
    return 0;
  });
};

export const sorByAscentTime = (list: Array<{ startTime: string }>) => {
  return list.sort((item1, item2) => {
    if (!item1.startTime) return -1;
    if (!item2.startTime) return 1;
    if (moment(item1.startTime).isBefore(item2.startTime)) return -1;
    return 0;
  });
};

export { convertDataFromReduxToServer, convertDataFromServerToRedux };
